import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TTeamMember } from 'types/group';
import s from './s.module.less';

export const PROVIDER_COLORS = [
    '#3B82F6',
    '#8B5CF6',
    '#F59E0B',
    '#10B981',
    '#EF4444',
    '#EC4899',
    '#14B8A6',
    '#6366F1',
    '#F97316',
    '#A78BFA',
    '#0EA5E9',
    '#D946EF',
    '#84CC16',
    '#F43F5E',
    '#06B6D4',
    '#E11D48',
    '#22C55E',
    '#7C3AED',
    '#FB923C',
    '#2DD4BF',
    '#4F46E5',
    '#CA8A04',
    '#0D9488',
    '#BE185D',
    '#65A30D',
    '#9333EA',
    '#EA580C',
    '#0891B2',
    '#DB2777',
    '#16A34A',
    '#7E22CE',
    '#C2410C',
    '#0E7490',
    '#9D174D',
    '#4D7C0F',
    '#6D28D9',
    '#B45309',
    '#155E75',
    '#831843',
    '#3F6212',
    '#5B21B6',
    '#9A3412',
    '#164E63',
    '#701A75',
    '#365314',
    '#4C1D95',
    '#7C2D12',
    '#134E4A',
    '#581C87',
    '#1E3A5F',
];

export type ViewingProvider = {
    id: number;
    providerId: number;
    name: string;
    color: string;
    isMe: boolean;
};

type Props = {
    teamMembers: TTeamMember[];
    currentProviderId?: number;
    currentProviderName?: string;
    selectedProviders: ViewingProvider[];
    onSelectedProvidersChange: (providers: ViewingProvider[]) => void;
};

const CloseIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
            d="M9 3L3 9M3 3l6 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CheckIcon = () => (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ViewingBar: React.FC<Props> = ({
    teamMembers,
    currentProviderId,
    currentProviderName,
    selectedProviders,
    onSelectedProvidersChange,
}) => {
    const [searchText, setSearchText] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const providerMembers = useMemo(() => {
        return teamMembers.filter((m) => m.providerId);
    }, [teamMembers]);

    const providerColorMap = useMemo(() => {
        const map: Record<number, string> = {};
        const otherColors = PROVIDER_COLORS.filter((c) => c !== '#3B82F6');
        let colorIndex = 0;
        providerMembers.forEach((m) => {
            if (m.providerId) {
                if (m.providerId === currentProviderId) {
                    map[m.providerId] = '#3B82F6';
                } else {
                    map[m.providerId] = otherColors[colorIndex % otherColors.length];
                    colorIndex++;
                }
            }
        });
        return map;
    }, [providerMembers, currentProviderId]);

    const filteredProviders = useMemo(() => {
        if (!searchText.trim()) return providerMembers;
        const q = searchText.toLowerCase();
        return providerMembers.filter((m) => {
            const name = `${m.firstName} ${m.lastName}`.toLowerCase();
            return name.includes(q);
        });
    }, [providerMembers, searchText]);

    const selectedIds = useMemo(() => {
        return new Set(selectedProviders.map((p) => p.providerId));
    }, [selectedProviders]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
                setSearchText('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleProvider = (member: TTeamMember) => {
        if (!member.providerId) return;
        const isMe = member.providerId === currentProviderId;

        if (selectedIds.has(member.providerId)) {
            if (selectedProviders.length <= 1) return;
            onSelectedProvidersChange(selectedProviders.filter((p) => p.providerId !== member.providerId));
        } else {
            const color = providerColorMap[member.providerId] || PROVIDER_COLORS[0];
            const newProvider: ViewingProvider = {
                id: member.id,
                providerId: member.providerId,
                name: `${member.firstName} ${member.lastName}`,
                color,
                isMe,
            };
            onSelectedProvidersChange([...selectedProviders, newProvider]);
        }
    };

    const removeProvider = (providerId: number) => {
        onSelectedProvidersChange(selectedProviders.filter((p) => p.providerId !== providerId));
    };

    return (
        <div className={s.bar}>
            <span className={s.label}>Viewing</span>

            {selectedProviders.map((p) => (
                <span key={p.providerId} className={s.badge} style={{ background: p.color }}>
                    {p.name}
                    {p.isMe ? ' (me)' : ''}
                    {selectedProviders.length > 1 && (
                        <button className={s.badgeClose} onClick={() => removeProvider(p.providerId)}>
                            <CloseIcon />
                        </button>
                    )}
                </span>
            ))}

            <div className={s.addWrap} ref={wrapRef}>
                <input
                    ref={inputRef}
                    type="text"
                    className={s.addInput}
                    placeholder="Add people…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onFocus={() => setDropdownOpen(true)}
                    autoComplete="off"
                />

                {dropdownOpen && (
                    <div className={s.dropdown}>
                        <div className={s.dropdownHeader}>Providers</div>
                        {filteredProviders.length === 0 && <div className={s.dropdownEmpty}>No providers found</div>}
                        {filteredProviders.map((m) => {
                            if (!m.providerId) return null;
                            const isSelected = selectedIds.has(m.providerId);
                            const color = providerColorMap[m.providerId] || PROVIDER_COLORS[0];
                            const isMe = m.providerId === currentProviderId;
                            const displayName = `${m.firstName} ${m.lastName}${isMe ? ' (me)' : ''}`;

                            return (
                                <div key={m.providerId} className={s.option} onClick={() => toggleProvider(m)}>
                                    <span className={s.optionBadge} style={{ background: color }}>
                                        {displayName}
                                    </span>
                                    <div className={`${s.check} ${isSelected ? s.checkSelected : ''}`}>
                                        {isSelected && <CheckIcon />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewingBar;
